/**
 * O Kovu que flutua no canto do painel — o `guardian` do Flutter.
 *
 * São três arquivos de lá num só: o `guardian_service.dart` (as falas), o
 * `guardian_controller.dart` (quando cada uma aparece) e a parte do
 * `kovu_mascot.dart` que não é desenho. O desenho fica em cada app, porque um
 * gato que flutua no navegador e um que flutua no celular não se animam com o
 * mesmo código; o relógio deste gato, sim, é o mesmo.
 *
 * Divergências do Flutter, todas medidas:
 *
 * - **As sete falas são constante, não asset.** Lá o `GuardianService` lê
 *   `assets/data/guardian_messages_seed.json` no arranque — e o próprio
 *   comentário de lá conta que antes elas moravam no Firestore e ficaram
 *   congeladas em inglês. São sete frases que nunca mudam: um `const` não tem
 *   arranque para falhar, e o relógio do watchOS já guarda as `frasesProntas`
 *   dele assim.
 * - **Sem `category`.** Cada fala de lá carrega `'idle'` ou `'reaction'`, e o
 *   `getRandomMessage()` é sempre chamado sem filtro — o campo nunca é lido. A
 *   fala de reação entra no sorteio ocioso lá e entra aqui.
 * - **Não repete a última.** Lá é `Random().nextInt(pool.length)` sobre as sete,
 *   então a mesma frase sai duas vezes seguidas uma vez em sete e parece que a
 *   caixinha não mudou. É o mesmo `evitar` do `Sorteio` de encontros.
 * - **O humor é de quem mora com você, não "do par".** Lá a fala de humor lê o
 *   `partnerUsername`, que pressupõe casa de duas pessoas. Aqui quem chama
 *   entrega os humores de hoje das outras pessoas da casa, quantas forem, e o
 *   Kovu fala de uma delas.
 * - **Sem `triggerMoodPrompt`.** Lá o guardião também acende o `MoodPicker` num
 *   canto da tela. Os dois painéis daqui já mostram a `EscolhaDeHumor` dentro da
 *   seção "Como estamos hoje" sempre que a pessoa ainda não respondeu — um
 *   segundo lugar para a mesma pergunta é pergunta repetida, não recurso.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Uma fala do Kovu parado. O `id` existe para não sortear a mesma duas vezes. */
export interface FalaDoKovu {
  id: string;
  texto: string;
}

/** As sete de `guardian_messages_seed.json`, na ordem de lá. */
export const FALAS_DO_KOVU: ReadonlyArray<FalaDoKovu> = [
  { id: 'fala_0', texto: 'Que bom ver vocês por aqui.' },
  { id: 'fala_1', texto: 'Toca em mim que eu abro a conversa inteira.' },
  { id: 'fala_2', texto: 'Tava esperando vocês dois.' },
  { id: 'fala_3', texto: 'Hoje o dia pede um plano juntos, hein?' },
  { id: 'fala_4', texto: 'Miau. *se enrosca na sua perna*' },
  { id: 'fala_5', texto: 'Se precisar de mim, é só chamar.' },
  { id: 'fala_6', texto: 'Guardei tudo o que vocês me contaram.' },
];

/** Uma fala ao acaso. `evitar` é o id da que acabou de sair. */
export function falaAoAcaso(evitar?: string | null): FalaDoKovu {
  const outras = evitar ? FALAS_DO_KOVU.filter((f) => f.id !== evitar) : FALAS_DO_KOVU;
  const lista = outras.length > 0 ? outras : FALAS_DO_KOVU;
  return lista[Math.floor(Math.random() * lista.length)];
}

/** O humor de hoje de alguém da casa, do jeito que o Kovu precisa dizê-lo. */
export interface HumorDaCasa {
  nome: string;
  emoji: string;
}

/** A fala de humor: o `'$partnerName está ${moodEmoji} hoje!'` de lá. */
export function falaDeHumor(humor: HumorDaCasa): string {
  return `${humor.nome} está ${humor.emoji} hoje!`;
}

/** De quantas em quantas falas o Kovu comenta o humor de alguém (`% 7` de lá). */
export const A_CADA = 7;

/** Quanto tempo a caixinha fica na tela. */
const MOSTRA = 5_000;

/** O `_reactionController`: 600ms de pulo, no arranque e a cada carinho. */
const PULO = 600;

/** O `Future.delayed(seconds: 2)` do `welcome()`: chegou, cumprimentou, sossegou. */
const SAUDACAO = 2_000;

/** O intervalo ocioso de lá: de 3 a 7 minutos, sorteado a cada volta. */
const ESPERA_MINIMA = 3;
const ESPERA_SORTEADA = 5;

export interface Guardiao {
  /** O que está escrito na caixinha agora, ou `null` quando ela está fechada. */
  fala: string | null;
  /** Se o pulo está correndo. Quem desenha decide o que fazer com isso. */
  pulando: boolean;
  /** Carinho: o `react()` de lá — pula e fala na hora. */
  carinho: () => void;
}

/**
 * O relógio do Kovu: entra pulando, fala sozinho de tempos em tempos, e fala na
 * hora quando recebe carinho.
 *
 * `humores` são os de hoje das outras pessoas da casa. Pode vir vazio: aí a
 * cada sete falas sai uma frase do baralho em vez do humor de alguém, que é o
 * mesmo que lá faz quando ninguém respondeu ainda.
 */
export function usarGuardiao(humores: ReadonlyArray<HumorDaCasa> = []): Guardiao {
  const [fala, setFala] = useState<string | null>(null);
  const [pulando, setPulando] = useState(true);

  const conta = useRef(0);
  const anterior = useRef<string | null>(null);
  /* Os humores entram por ref porque mudam a cada documento que chega, e o
     relógio ocioso não pode ser rearmado toda vez que alguém diz como está —
     seriam três minutos que nunca chegam ao fim. */
  const daCasa = useRef(humores);
  daCasa.current = humores;

  const ocioso = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sumir = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulo = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dizer = useCallback(() => {
    conta.current += 1;
    const gente = daCasa.current;
    let texto: string;
    if (conta.current % A_CADA === 0 && gente.length > 0) {
      texto = falaDeHumor(gente[Math.floor(Math.random() * gente.length)]);
    } else {
      const f = falaAoAcaso(anterior.current);
      anterior.current = f.id;
      texto = f.texto;
    }
    setFala(texto);
    if (sumir.current) clearTimeout(sumir.current);
    sumir.current = setTimeout(() => setFala(null), MOSTRA);
  }, []);

  const esperar = useCallback(() => {
    if (ocioso.current) clearTimeout(ocioso.current);
    const minutos = ESPERA_MINIMA + Math.floor(Math.random() * ESPERA_SORTEADA);
    ocioso.current = setTimeout(
      () => {
        dizer();
        esperar();
      },
      minutos * 60 * 1_000,
    );
  }, [dizer]);

  useEffect(() => {
    // O `addPostFrameCallback` de lá: chega pulando e só depois se aquieta.
    pulo.current = setTimeout(() => setPulando(false), PULO);
    const abertura = setTimeout(esperar, SAUDACAO);
    return () => {
      clearTimeout(abertura);
      if (pulo.current) clearTimeout(pulo.current);
      if (ocioso.current) clearTimeout(ocioso.current);
      if (sumir.current) clearTimeout(sumir.current);
    };
  }, [esperar]);

  const carinho = useCallback(() => {
    setPulando(true);
    if (pulo.current) clearTimeout(pulo.current);
    pulo.current = setTimeout(() => setPulando(false), PULO);
    dizer();
  }, [dizer]);

  return { fala, pulando, carinho };
}
